import { useState, useEffect } from "react";
import "../reactSelectStyles.css";

import { CREW_ROLES } from "../constants/constants";
import fetchFromAPI from "../utils/fetchFromAPI";
import postToAPI from "../utils/postToAPI";
import { validateForm } from "../utils/utils";

import CreatableSelectField from "./CreatableSelectField";

const SubmissionForm = () => {
  const [nameOptions, setNameOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [submissionError, setSubmissionError] = useState(null);
  const [crewValues, setCrewValues] = useState({});


  useEffect(() => {
    fetchNameOptions();
  }, []);

  const handleCrewChange = (name, values) => {
    setCrewValues(prev => ({ ...prev, [name]: values }));
  };

  const fetchNameOptions = async () => {
    try {
      // Use the new dedicated names endpoint
      const response = await fetchFromAPI("/api/names");
      const names = response.data; // This is now just an array of names

      const options = names.map((name) => ({
        value: name,
        label: name,
      }));

      setNameOptions(options);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching name options:", error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = Object.fromEntries(new FormData(e.target));

    // Get boolean rather than on/off for isFirstTimeFilmmaker
    const isFirstTimeFilmmaker = e.target.isFirstTimeFilmmaker.checked;
    formData["isFirstTimeFilmmaker"] = isFirstTimeFilmmaker;


    // Convert stringified arrays to real arrays
    formData.directors = crewValues.directors || [];
    CREW_ROLES.forEach(role => {
      formData[role.name] = crewValues[role.name] || [];
    });

    console.log("Form data before validation:", formData);
    // Validate form data
    const submissionErrors = validateForm(formData);
    if (submissionErrors.length > 0) {
      setSubmissionError(submissionErrors.join(" "));
      setSubmissionStatus(null);
      return;
    }
    else {
      setSubmissionError(null);
    }

    setIsSubmitting(true);
    setSubmissionStatus(null);
    try {
      const timestamp = new Date().toISOString();
      formData["timestamp"] = timestamp;
      await postToAPI("/api/submissionsform", formData);
      setSubmissionStatus("success");
      setSubmissionError(null);
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmissionError("An error occurred while submitting the form. Please try again.");    
      setSubmissionStatus(null);
    }

    setIsSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div>
        <label htmlFor="filmTitle" className="form-label">
          What is the Title of the Film?
        </label>
        <input
          type="text"
          id="filmTitle"
          name="filmTitle"
          placeholder="Film Title"
        />
      </div>
      {loading ? (
        <p>Loading names...</p>
      ) : (
        <CreatableSelectField
          name={"directors"}
          label={"Please list the Director(s) of this film."}
          onChange={(values) => handleCrewChange("directors", values)}
          nameOptions={nameOptions}
          setNameOptions={setNameOptions}
        />
      )}

      <div style={{ paddingBottom: "10px" }}>
        <div style={{ paddingBottom: "8px" }}>
          <label htmlFor="isFirstTimeFilmmaker" className="form-label">
            First-time filmmaker?
          </label>
          <input
            id="isFirstTimeFilmmaker"
            name="isFirstTimeFilmmaker"
            type="checkbox"
          />
        </div>
        <span>
          This is the first film that I have ever directed in my life.
        </span>
      </div>
      <div>
        <label className="form-label">
          What borough was the film primarily shot in?
        </label>
        <select name={"borough"} defaultValue="" placeholder="Select a borough">
          <option value="" disabled hidden>
            Select a Borough
          </option>
          <option value="Manhattan">Manhattan</option>
          <option value="Brooklyn">Brooklyn</option>
          <option value="Queens">Queens</option>
          <option value="The Bronx">The Bronx</option>
          <option value="Staten Island">Staten Island</option>
          <option value="Other">Other</option>
        </select>
      </div>

      {CREW_ROLES.map((role) => (
        <CreatableSelectField
          key={role.name}
          name={role.name}
          onChange={(values) => handleCrewChange(role.name, values)}
          label={role.label}
          nameOptions={nameOptions}
          setNameOptions={setNameOptions}
          tooltipInfo={role.tooltipInfo}
        />
      ))}

      <div>
        <label htmlFor="videoLink" className="form-label">
          Please share the link for your film.
        </label>
        <input type="text" name="videoLink" placeholder="Video Link" />
      </div>
      <div>
        <label htmlFor="present" className="form-label">
          Will you (the director) be present at the next screening? We will do
          our best to screen it if not, but the director must be present for it
          to be prioritized.{" "}
          <a
            target="_blank"
            href="https://www.meetup.com/short-cuts/"
            style={{ textDecoration: "none", color: "#e7b0a6" }}
          >
            RSVP here
          </a>
        </label>
        <select name="present">
          <option value="">Will the director be present?</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
      </div>
      <div>
        <label htmlFor="FormRuleInfo" className="form-label">
          Where is the Form (rule #1) in your film? Provide description and/or
          timestamp.
        </label>
        <textarea
          name="FormRuleInfo"
          className="form-input"
          rows="3"
        ></textarea>
      </div>
      <div>
        <label htmlFor="WildCardInfo" className="form-label">
          Where is the Wildcard (rule #2) in your film? Provide description
          and/or timestamp.
        </label>
        <textarea
          name="WildCardInfo"
          className="form-input"
          rows="3"
        ></textarea>
      </div>
      <div>
        <label htmlFor="additionalInfo" className="form-label">
          Do you have anything you'd like to add about your experience making
          this film, being a part of Short Cuts, or anything else you'd like us
          to know about your film?
        </label>
        <textarea
          name="additionalInfo"
          className="form-input"
          rows="5"
        ></textarea>
      </div>
      <button
        type="submit"
        disabled={isSubmitting}
        style={{ cursor: isSubmitting ? "wait" : "pointer" }}
      >
        {isSubmitting ? "Submitting..." : "Submit"}
      </button>
      {submissionError && (
        <div className="alert error">
          <span role="img" aria-label="Error">❌</span>          
          {submissionError}
        </div>
      )}
      {submissionStatus === "success" && (
        <div className="alert success">
          <span role="img" aria-label="Success">
            ✅
          </span>{" "}
          Submission successful!
        </div>
      )}
    </form>
  );
};

export default SubmissionForm;
